import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, type FormInstance, message, Modal, Space } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';

import client from '@/lib/feathers';

import type { ExtraUserInfoFormat } from './types';

const { confirm } = Modal;

const chineseNums = [
  '零',
  '一',
  '二',
  '三',
  '四',
  '五',
  '六',
  '七',
  '八',
  '九',
];

const chineseUnits = ['', '十', '百', '千', '萬', '億'];

const numberToChinese = (number: number) => {
  let result = '';

  const numString = number.toString();

  const length = numString.length;

  for (let i = 0; i < length; i++) {
    const digit = parseInt(numString[i]);
    const unit = length - i - 1;

    if (digit === 0) {
      continue;
    }

    if (length === 2 && i === 0 && digit === 1) {
      result += chineseUnits[unit];
    } else {
      result += chineseNums[digit] + chineseUnits[unit];
    }
  }

  return result;
};

const getUpdateData = (
  orderId: number,
  extraUserInfoFormat: ExtraUserInfoFormat[]
) => {
  const updateData = extraUserInfoFormat.reduce(
    (updateData: ExtraUserInfoFormat[], question) => {
      if (question.orderId < orderId) {
        updateData.push({ ...question });
      }
      if (question.orderId > orderId) {
        updateData.push({
          ...question,
          orderId: question.orderId - 1,
        });
      }
      if (question.orderId === orderId) {
        updateData.push({
          ...question,
          orderId: extraUserInfoFormat.length - 1,
          isDeleted: true,
        });
      }

      return updateData;
    },
    []
  );

  return updateData;
};

const showDeleteConfirm = (args: {
  orderId: number;
  communityDataId: { $oid: string };
  extraUserInfoFormat: ExtraUserInfoFormat[];
  updateCommunityData: () => void;
}) => {
  const { orderId, communityDataId, extraUserInfoFormat, updateCommunityData } =
    args;

  confirm({
    title: '確定刪除該問題?',
    icon: <ExclamationCircleOutlined />,
    content: '確定的話，請按確認鍵',
    onOk: async () => {
      try {
        const updateData = getUpdateData(orderId, extraUserInfoFormat);
        await client
          .service('communityData')
          .patch(communityDataId, { extraUserInfoFormat: updateData });
        updateCommunityData();
        message.success('刪除問題成功!');
      } catch (error) {
        message.error('刪除問題失敗!');
      }
    },
  });
};

const handleEditButtonClick = (args: {
  record: ExtraUserInfoFormat & {
    category: string;
  };
  form: FormInstance;
  openEditQuestionModal: () => void;
}) => {
  const { record, form, openEditQuestionModal } = args;
  form.setFieldsValue(record);
  openEditQuestionModal();
};

export const getColumns = (args: {
  tableType: 'memberSignUp' | 'infoCollection';
  communityDataId?: { $oid: string };
  extraUserInfoFormat?: ExtraUserInfoFormat[];
  updateCommunityData?: () => void;
  form?: FormInstance;
  openEditQuestionModal?: () => void;
}) => {
  const {
    tableType,
    extraUserInfoFormat,
    communityDataId,
    updateCommunityData,
    form,
    openEditQuestionModal,
  } = args;

  const columns: ColumnsType<ExtraUserInfoFormat & { category: string }> = [
    {
      title: '編號',
      dataIndex: 'orderId',
      render: (value) => {
        const orderId = numberToChinese(value + 1);
        return '問題' + orderId;
      },
    },
    {
      title: '類別',
      dataIndex: 'category',
    },
    {
      title: '問題',
      dataIndex: 'name',
    },
    {
      title: '選/必填',
      dataIndex: 'isRequired',
      render: (value) => {
        return value ? '必填' : '選填';
      },
    },
  ];

  if (tableType === 'infoCollection') {
    columns.push({
      title: '操作',
      dataIndex: 'actions',
      fixed: 'right',
      width: 180,
      render: (_value, record) => {
        return (
          <>
            {record.category === '自訂' && (
              <Space>
                <Button
                  onClick={() => {
                    if (form && openEditQuestionModal) {
                      handleEditButtonClick({
                        record,
                        form,
                        openEditQuestionModal,
                      });
                    }
                  }}
                >
                  編輯
                </Button>
                <Button
                  type="primary"
                  danger
                  onClick={() => {
                    if (
                      extraUserInfoFormat &&
                      communityDataId &&
                      updateCommunityData
                    ) {
                      showDeleteConfirm({
                        orderId: record.orderId,
                        communityDataId,
                        extraUserInfoFormat,
                        updateCommunityData,
                      });
                    }
                  }}
                >
                  刪除
                </Button>
              </Space>
            )}
          </>
        );
      },
    });
  }

  return columns;
};

export const defaultSignUpQuestions = [
  {
    orderId: 0,
    category: '系統預設',
    name: '手機號碼',
    isRequired: true,
  },
];

export const defaultQuestions = [
  '圖片',
  '姓名',
  '生日',
  '性別',
  '電子郵件',
  '手機號碼',
];

export const defaultMemberInfoQuestions = [
  {
    orderId: 0,
    category: '系統預設',
    name: '圖片',
    isDefault: true,
    isRequired: true,
    isDeleted: false,
  },
  {
    orderId: 1,
    category: '系統預設',
    name: '姓名',
    isDefault: true,
    isRequired: true,
    isDeleted: false,
  },
  {
    orderId: 2,
    category: '系統預設',
    name: '生日',
    isDefault: true,
    isRequired: true,
    isDeleted: false,
  },
  {
    orderId: 3,
    category: '系統預設',
    name: '性別',
    isDefault: true,
    isRequired: true,
    isDeleted: false,
  },
  {
    orderId: 4,
    category: '系統預設',
    name: '電子郵件',
    isDefault: true,
    isRequired: true,
    isDeleted: false,
  },
];

export const getOrderIdOptions = (
  customQuestions: (ExtraUserInfoFormat & { category: string })[]
) => {
  const optionalQuestionsLength = customQuestions.length;
  const optionsNumber = optionalQuestionsLength + 1;
  const orderIdOptions = Array.from({ length: optionsNumber }, (_, index) => ({
    label: 6 + index,
    value: 6 + index,
  }));

  return orderIdOptions;
};

export const isRequiredOptions = [
  {
    label: '必填',
    value: true,
  },
  {
    label: '選填',
    value: false,
  },
];

export const getChineseOrderId = (number: number) => {
  const chineseOrderId = numberToChinese(number + 1);
  return '問題' + chineseOrderId;
};

export const checkQuestionIsExistInDatabase = (
  questions: ExtraUserInfoFormat[],
  name: string
) => {
  const isExisted = questions.some((question) => {
    return question.name === name;
  });
  return isExisted;
};

export const sortQuestions = (
  a: ExtraUserInfoFormat,
  b: ExtraUserInfoFormat
) => {
  return a.orderId - b.orderId;
};

export const validateIsNameDuplicate = (
  questions: ExtraUserInfoFormat[],
  name: string
) => {
  const isDuplicate = questions.some(
    (question) => question.name === name && question.isDeleted === false
  );

  return isDuplicate;
};
